import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/esm/Alert';
import Button from 'react-bootstrap/esm/Button';
import Spinner from 'react-bootstrap/esm/Spinner';
import ButtonGroup from 'react-bootstrap/esm/ButtonGroup';
import { useSelector } from 'react-redux';
import { Api } from '../../api';
import { SingleUser, UserRelationRoleEnum } from '../../api/types';
import { ApplicationState } from '../../app/store';
import { DashboardLayout } from '../../layouts/dashboard-layout/DashboardLayout';
import { ETableColumnAlign, ResponsiveTable, ResponsiveTableColumn } from '../../features/responsive-table/ResponsiveTable';
import { ERouterPath } from '../../features/router/ERouterPath';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { LibraryItem } from '../../models/LibraryItem';
import { ISplitLayoutItem, SplitSubLayout } from '../../layouts/split-sub-layouts/SplitSubLayout';
import { Icon } from '../../features/icon/Icon';
import { UserDisplay } from '../../features/user-display/UserDisplay';
import './Library.scss';
import { Search } from '../../features/search/Search';
import { LibrarySubmissionList } from './LibrarySubmissionList';
import { IDossierV2 } from '../../models/DossierV2';
import { DossierMember, DossierMemberInterface } from '../../models/DossierMember';
import classNames from 'classnames';
import { RoundButton } from '../../features/round-button/RoundButton';

interface ILibraryTableData {
  name: JSX.Element;
  createdAt: string;
  buttons: JSX.Element;
}

export const Library = (): JSX.Element => {
  const dossier = useSelector<ApplicationState, IDossierV2|undefined>(state => state.session.dossier);
  const relation = useSelector<ApplicationState, DossierMemberInterface|undefined>(state => state.session.relation);
  const user = useSelector<ApplicationState, SingleUser|undefined>(state => state.session.user);
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [allLibraryItems, setAllLibraryItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dossierRelationId, setDossierRelationId] = useState<number|undefined>(undefined);
  const [selectableDossierRelations, setSelectableDossierRelations] = useState<DossierMember[]>([]);
  const [splitLayoutSidebarItems, setSplitLayoutSidebarItems] = useState<ISplitLayoutItem[]>([]);
  const [searchKeys, setSearchKeys] = useState<string | undefined>(undefined);
  const [results, setResults] = useState<boolean>(false);
  const [displayAll, setDisplayAll] = useState<boolean>(false);
  const [submittingItemId, setSubmittingItemId] = useState<number|undefined>(undefined);
  const isCaregiver = useMemo(() => relation?.role === UserRelationRoleEnum.caregiver, [relation]);
  const [isActive, setIsActive] = useState<boolean>(false);

  const columns: ResponsiveTableColumn<ILibraryTableData>[] = [
    { title: 'Titel', objectKey: 'name', width: '22rem', disableSort: true },
    { title: 'Datum', objectKey: 'createdAt', width: '6rem', disableSort: true },
  ];

  if (!dossier?.archived && isCaregiver) {
    columns.push({ title: '', objectKey: 'buttons', width: '2rem', disableSort: true, horizontalAlign: ETableColumnAlign.END, hideBorder: true });
  }

  const handleSelectRelation = useCallback((relation: DossierMember) => {
    if (dossierRelationId === relation.memberId) {
      return;
    }

    setDisplayAll(false);
    setLoading(true);
    setResults(false);
    setIsActive(true);
    setDossierRelationId(relation.memberId);
  }, [dossierRelationId]);

  const handleSearch = (value: string): void => {
    setSearchKeys(value);
  };

  const getRouterPathToMember = (memberId: number) => ERouterPath.libraryItemsForRelation.replace(':relationId', `${memberId}`);

  useEffect(() => {
    if (!dossier || !user) {
      return;
    }

    Api.listDossierRelations(dossier.id)
      .then(relations => {
        const thisRelation = relations.find(relation => relation.user.id === user.id);
        if (!thisRelation) {
          throw new Error('Failed to get current dossier relation');
        }
        if (thisRelation.role === UserRelationRoleEnum.caregiver) {
          const selectableRelations = relations.filter(relation => {
            const isNotThisUser = relation.user.id !== user.id;
            const isNotCaregiver = relation.role !== UserRelationRoleEnum.caregiver;
            return isNotThisUser && isNotCaregiver;
          });
          setSelectableDossierRelations(selectableRelations);
          if (selectableRelations.length === 1) {
            setDossierRelationId(selectableRelations[0].user.id);
          } else {
            setLoading(false);
          }
        } else {
          setDossierRelationId(thisRelation.memberId);
        }
      })
      .catch(err => {
        console.error(err);
        NotificationHelper.toastError('Ophalen oefeningen mislukt, probeer het later opnieuw.');
      });
  }, [dossier, user]);

  useEffect(() => {
    if (!dossierRelationId) {
      return;
    }

    if (isCaregiver) {
      Api.getAllLibraryItems()
        .then(items => setAllLibraryItems(items))
        .catch(err => {
          console.error(err);
        });
    }

    Api.getLibraryItems(dossierRelationId)
      .then(items => setLibraryItems(items))
      .catch(err => {
        console.error(err);
        NotificationHelper.toastError('Ophalen oefeningen mislukt, probeer het later opnieuw.');
      })
      .finally(() => setLoading(false));
  }, [dossierRelationId, isCaregiver]);

  useEffect(() => {
    // Create the sidebar items
    const relationItems: ISplitLayoutItem[] = selectableDossierRelations.map(relation => ({
      className: 'library-relation-select__relation',
      selectedClassName: 'library-relation-select__relation--selected',
      element: <UserDisplay className="documents-back-button__element" user={relation.user} subTitle={relation.getRoleText({ capitalize: true })} small={true}/>,
      mobileBackButtonElement: <div className="documents-back-button" onClick={() => setIsActive(false)}>
        <Icon className="documents-back-button__back-icon" icon="back" />
        <UserDisplay className="documents-back-button__element" user={relation.user} subTitle={relation.getRoleText({ capitalize: true })} small={true}/>
      </div>,
      routerPath: getRouterPathToMember(relation.memberId),
      onSelect: () => handleSelectRelation(relation),
    }));

    setSplitLayoutSidebarItems(relationItems);
  }, [selectableDossierRelations, handleSelectRelation]);

  const handleAddLibraryItem = useCallback((libraryItem: LibraryItem): void => {
    if (!dossierRelationId) {
      return;
    }
    setSubmittingItemId(libraryItem.id);
    Api.addLibraryItemToDossierRelation({ libraryItemId: libraryItem.id, dossierRelationId })
      .then(() => {
        setLibraryItems([libraryItem, ...libraryItems]);
      })
      .catch(err => {
        console.error('Failed to add library item', err);
        NotificationHelper.toastError('Toevoegen oefening mislukt door een onverwachte fout');
      })
      .finally(() => setSubmittingItemId(undefined));
  }, [dossierRelationId, libraryItems]);

  const handleRemoveLibraryItem = useCallback((libraryItem: LibraryItem): void => {
    if (!dossierRelationId) {
      return;
    }
    setSubmittingItemId(libraryItem.id);
    Api.removeLibraryItemFromDossierRelation({ libraryItemId: libraryItem.id, dossierRelationId })
      .then(() => {
        setLibraryItems(libraryItems.filter(item => item.id !== libraryItem.id));
      })
      .catch(err => {
        console.error('Failed to remove library item', err);
        NotificationHelper.toastError('Verwijderen oefening mislukt door een onverwachte fout');
      })
      .finally(() => setSubmittingItemId(undefined));
  }, [dossierRelationId, libraryItems]);

  const filter = (item: LibraryItem): boolean => {
    if (!searchKeys || typeof searchKeys !== 'string') {
      return true;
    }
    const sanitizedKeys = searchKeys?.trim().toLowerCase();
    if (!sanitizedKeys) {
      return true;
    } else {
      return item.title.toLowerCase().includes(sanitizedKeys);
    }
  };

  const filterNonSelectedItems = (item: LibraryItem): boolean => !libraryItems.some(selectedItem => selectedItem.id === item.id);

  let data: ILibraryTableData[] = [];
  let dataAllItems: ILibraryTableData[] = [];
  if (!displayAll) {
    data = libraryItems.filter(filter).map(item => ({
      name: <a className="library-item__link" href={item.url} target="_blank" rel="noreferrer">{item.title}</a>,
      createdAt: item.createdAt.toLocaleDateString('nl-NL'),
      buttons: <RoundButton type="remove" label="Verwijderen" loading={submittingItemId === item.id} onClick={() => handleRemoveLibraryItem(item)} />,
    }));
  } else {
    dataAllItems = allLibraryItems.filter(filterNonSelectedItems).filter(filter).map(item => ({
      name: <a className="library-item__link" href={item.url} target="_blank" rel="noreferrer">{item.title}</a>,
      createdAt: item.createdAt.toLocaleDateString('nl-NL'),
      buttons: <RoundButton type="add" label="Toevoegen" loading={submittingItemId === item.id} onClick={() => handleAddLibraryItem(item)} />,
    }));
  }

  const renderSubmissionList = () => {
    if (!dossierRelationId) {
      return <Alert className="library__info" variant="info">Selecteer een persoon</Alert>;
    }
    return <>
      <div className="library-flex-container library-flex-container--submissions">
        <div className="library-table">
          <LibrarySubmissionList dossierRelationId={dossierRelationId} />
        </div>
        <Button variant="link" className="library-item__button" onClick={() => setResults(false) }>Toon oefeningen</Button>
      </div>
    </>;
  };

  const renderData = () => {
    let content: JSX.Element;
    if (!dossier) {
      content = <Alert className="library__info" variant="info">Geen dossier geselecteerd</Alert>;
    } else if (!dossierRelationId) {
      content = <Alert className="library__info" variant="info">Selecteer een persoon</Alert>;
    } else if (loading) {
      content = <Spinner animation="border" variant="primary" />;
    } else if (data.length === 0 && !displayAll) {
      content = <Alert className="library__info" variant="info">Er zijn nog geen oefeningen { isCaregiver ? 'geselecteerd' : 'beschikbaar' }</Alert>;
    } else {
      content = <>
        <div className="library-table">
          { displayAll ? <ResponsiveTable<ILibraryTableData> columns={columns} data={dataAllItems}/> : <ResponsiveTable<ILibraryTableData> columns={columns} data={data}/> }
        </div>
        <Button variant="link" className="library-item__button" onClick={() => setResults(true) }>Toon ingevulde formulieren</Button>
      </>;
    }

    return <>
      <div className="sort-search-container sort-search-container--library">
        <Search onSearch={handleSearch} placeholder="Zoek in oefeningen"/>
        {
          (!dossier?.archived && isCaregiver) &&
          <ButtonGroup className="library__display-button-group">
            <Button className={classNames('library__display-button', 'library__display-button--only-selected', { 'library__display-button--active': !displayAll })} onClick={() => setDisplayAll(false)}>Geselecteerd</Button>
            <Button className={classNames('library__display-button', 'library__display-button--all', { 'library__display-button--active': displayAll })} onClick={() => setDisplayAll(true)}>Niet geselecteerd</Button>
          </ButtonGroup>
        }
      </div>
      <div className="library-flex-container">
        {content}
      </div>
    </>;
  };

  return (
    <DashboardLayout grayBackgroundMobile={isActive}>
      <div className="page-title">Oefeningen</div>
      <SplitSubLayout items={splitLayoutSidebarItems} rootRouterPath={ERouterPath.library}>
        { results ? renderSubmissionList() : renderData() }
      </SplitSubLayout>
    </DashboardLayout>
  );
};
